import { AuthedButton } from '@/client/components/authed-button';
import { CurrencyView, CurrencyViewProps } from '@/client/components/currency-view';
import { UserAvatar } from '@/client/components/user-avatar';
import { useAuth } from '@/client/hooks/use-auth';
import { useSignedUserAssets } from '@/client/hooks/use-signed-user-assets';
import { useSignedUserInfo } from '@/client/hooks/use-signed-user-info';
import { useSingerTokenBalance } from '@/client/hooks/use-signed-token-balance';
import { useAppSettingStore } from '@/client/store/app-store';
import { useDidAuthStore } from '@/client/store/did-auth-store';
import { useModelStore } from '@/client/store/model-store';
import { cn } from '@/client/utils';
import { api } from '@/client/utils/api';
import { useTranslation } from 'react-i18next';
import useMedia from 'react-use/lib/useMedia';

export const AssetsCurrencyView = ({ title, pnl, className, ...props }: { title: string; pnl?: string; className?: string } & CurrencyViewProps) => {
  return (
    <dl className={cn('flex flex-col text-start', className)}>
      <dt className="text-foreground/70 text-xs leading-4 font-medium whitespace-nowrap">{title}</dt>
      <dd className="font-mono text-sm leading-5 md:text-base md:leading-5 font-semibold flex items-center gap-1 whitespace-nowrap tabular-nums">
        <CurrencyView showIcon={true} animated={true} {...props} />
        {pnl && Math.abs(Number(pnl)) > 0 && (
          // No `animated`: the animated path formats isChange as a percent, which
          // renders this absolute PnL amount as "+5,000%". Non-animated keeps the
          // signed amount + up/down color, matching the portfolio card.
          <CurrencyView
            className="text-xs leading-4"
            {...props}
            isChange={true}
            value={pnl}
          />
        )}
      </dd>
    </dl>
  );
};

export const UserInfo = () => {
  const { t } = useTranslation();
  const setWalletOpen = useModelStore(state => state.setWalletOpen);
  const { isAuthed, login } = useAuth();
  const { data: assets } = useSignedUserAssets(api);
  const { data: singerInfo } = useSignedUserInfo(api);
  // DID demo identity overrides display name/avatar when present (sharp-edges S1:
  // display only — no capability is granted from this).
  const didIdentity = useDidAuthStore(state => state.identity);
  const baseToken = useAppSettingStore(state => state.baseToken);
  const tokenBalances = useSingerTokenBalance(api);
  const onChainBalance = tokenBalances.data?.find(item => item.baseTokenAddress === baseToken.address);
  const isDesktop = useMedia('(min-width: 768px)');

  // Not authenticated: show Sign In button only
  if (!isAuthed) {
    return (
      <AuthedButton
        variant="default"
        onClick={login}
        size={isDesktop ? 'default' : 'sm'}
        className="btn-brand"
      >
        {t('sign-in')}
      </AuthedButton>
    );
  }

  // Authenticated: show Portfolio, Balance, and Avatar.
  // Self-custody (connected wallet): no platform Deposit — the user already
  // holds and controls their USDT in their own wallet.
  return (
    <>
      <AuthedButton
        showAuth={false}
        showLoading={false}
        variant="ghost"
        onClick={() => setWalletOpen(true)}
        className="px-0 h-full flex items-center justify-center hover:bg-transparent gap-4 md:gap-6"
      >
        <AssetsCurrencyView title={t('portfolio')} value={assets?.value ?? 0} pnl={assets?.pnl} decimal={assets?.decimal} className="min-w-[80px]" />
        <AssetsCurrencyView
          title={t('balance')}
          value={onChainBalance?.balance ?? assets?.balance}
          decimal={onChainBalance?.baseTokenDecimals ?? assets?.decimal}
          isLoading={tokenBalances.isPending || (onChainBalance == null && assets?.balance == null)}
          className="min-w-[64px]"
        />
      </AuthedButton>
      <AuthedButton
        showLoading={!singerInfo}
        variant="ghost"
        onClick={() => setWalletOpen(true)}
        size={isDesktop ? undefined : 'sm'}
        className={cn(singerInfo != undefined && 'px-0 bg-transparent hover:bg-transparent')}
      >
        <UserAvatar
          avatar={didIdentity?.picture ?? singerInfo?.avatar}
          address={didIdentity?.walletAddress ?? singerInfo?.uid}
          fallback={didIdentity?.name ?? singerInfo?.name}
          className="size-9 cursor-pointer duration-200 hover:ring-2 hover:ring-offset-2 ring-offset-background ring-blue-[#C0D4FF]"
        />
      </AuthedButton>
    </>
  );
};
